import React, { useState } from 'react';
import { HelpCircle, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpModal } from './HelpModal';
import { cn } from '@/lib/utils';

interface HelpIconProps {
  tooltip?: string;
  modalTitle?: string;
  modalContent?: string | React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'subtle';
}

export const HelpIcon: React.FC<HelpIconProps> = ({
  tooltip,
  modalTitle,
  modalContent,
  className,
  size = 'lg',
  variant = 'default',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const colorClasses = {
    default: 'text-blue-600 hover:text-blue-700',
    subtle: 'text-blue-500 hover:text-blue-600',
  };

  const handleClick = () => {
    if (modalTitle && modalContent) {
      setIsModalOpen(true);
    }
  };

  // If only tooltip, show tooltip on hover
  if (tooltip && !modalTitle && !modalContent) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(
                'inline-flex items-center justify-center cursor-help rounded-full p-0.5 hover:bg-blue-50 transition-colors',
                colorClasses[variant],
                className
              )}
              aria-label="Help"
            >
              <HelpCircle className={sizeClasses[size]} />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // If modal available, show clickable icon
  if (modalTitle && modalContent) {
    return (
      <>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleClick}
                className={cn(
                  'inline-flex items-center justify-center cursor-pointer transition-colors rounded-full p-1 hover:bg-blue-50',
                  colorClasses[variant],
                  className
                )}
                aria-label="Show help"
              >
                <Info className={sizeClasses[size]} />
              </button>
            </TooltipTrigger>
            {tooltip && (
              <TooltipContent>
                <p className="max-w-xs">{tooltip}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        <HelpModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={modalTitle}
          content={modalContent}
        />
      </>
    );
  }

  // Fallback: just show icon
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center justify-center cursor-help rounded-full p-0.5 hover:bg-blue-50 transition-colors',
        colorClasses[variant],
        className
      )}
      aria-label="Help"
    >
      <HelpCircle className={sizeClasses[size]} />
    </button>
  );
};

